import { AESDecrypt, AESEncrypt } from 'utils/Utils';
import jwt from 'jsonwebtoken'


const baseURL = process.env.REACT_APP_API_HOST;

class AuthService {
    // Login
    login = async (email, password) => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
            timeout: 3000
        };

        return fetch(`${baseURL}/auth/login`, requestOptions)
            .then(this.handleResponse)
            .then(response => {
                this.saveSession(response)
                return response;
            });
    }

    // LogOut
    logout() {
        this.clearSession()
    }

    // ---- Local Session ----
    saveSession = async (data) => {
        localStorage.setItem("bf__t", AESEncrypt(data?.tokens.access.token));
        localStorage.setItem("bf__rt", AESEncrypt(data?.tokens.refresh.token));
        localStorage.setItem("bf__u_i", JSON.stringify(data.teamUser));
        return !!(localStorage?.getItem('bf__t'))
    }

    clearSession() {
        localStorage.clear()
    }

    getAccessToken = () => {
        return AESDecrypt(localStorage.getItem("bf__t"));
    }

    getRefreshToken = async () => {
        return AESDecrypt(localStorage.getItem("bf__rt"))
    }

    getCurrentUser = () => {
        let user = localStorage.getItem("bf__u_i");
        return JSON.parse(user);
    }

    // Returns the current user's permissions array from the stored session.
    // The login response now includes teamUser.role populated with { id, name, permissions }.
    getUserPermissions = () => {
        const user = localStorage.getItem("bf__u_i");
        if (!user) return [];
        const parsedUser = JSON.parse(user);
        return parsedUser?.role?.permissions ?? [];
    }

    hasPermission = (permissionKey) => {
        return this.getUserPermissions().includes(permissionKey);
    }

    // @deprecated — kept for backward compat during transition. Use hasPermission() instead.
    getUserAuthority = () => {
        const user = localStorage.getItem("bf__u_i");
        if (user) {
            const parsedUser = JSON.parse(user);
            return parsedUser?.role?.id ?? null;
        }
        return null;
    }

    // @deprecated — kept for backward compat during transition. Use hasPermission() instead.
    isUserAuthorized = (roles) => {
        const currentUserRole = this.getUserAuthority();
        return roles && roles.indexOf(currentUserRole) !== -1;
    }

    updateAccessToken(token) {
        localStorage.setItem("bf__t", AESEncrypt((token)));
        return !!(localStorage?.getItem('bf__t'))
    }

    updateResfreshAccessToken(token) {
        localStorage.setItem("bf__rt", AESEncrypt(token));
        return !!(localStorage?.getItem('bf__rt'))
    }

    handleResponse(response) {
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if (!response.ok) {
                if ([401, 403].indexOf(response.status) !== -1) {
                    localStorage.clear()
                }
                const error = data?.message || response.statusText;
                return Promise.reject(error);
            }
            return data;
        });
    }

    isValidSession = () => {
        if (this.isValidJWT(this.getAccessToken())) {
            return true;
        }
        // Access token expired — keep the session alive if a refresh token exists.
        // The Axios interceptor will silently refresh on the next 401.
        const refreshToken = AESDecrypt(localStorage.getItem("bf__rt"));
        if (refreshToken) {
            return true;
        }
        this.clearSession();
        return false;
    }

    isValidJWT = (token) => {
        if (token) {
            let decoded = jwt.decode(token);
            return new Date(decoded.exp * 1000) > new Date();
        } else {
            return false
        }
    };

    fetchWithTimeout = async (resource, options = {}) => {
        const { timeout = 3000 } = options;
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        const response = await fetch(resource, {
            ...options,
            signal: controller.signal
        });
        clearTimeout(id);
        return response;
    }
}

const authService = new AuthService();
export default authService;
